import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import BarChartIcon from "@mui/icons-material/BarChart";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import HeaderRec from "../../header/headerRec";
import PaginatedList from "./PaginatedList";
import './StatistiquesPage.css';
import Footer from '../../commun/Footer';
import LoadingPopup from "../../commun/LoadingPopup";

const MultiDeviseAmount = ({ montantObj }) => (
  <Box className="multi-devise-amount">
    {montantObj &&
      Object.entries(montantObj).map(
        ([devise, montant]) =>
          montant > 0 && (
            <Typography key={devise} variant="h6" className="montant-texte">
              {montant.toLocaleString()} {devise}
            </Typography>
          )
      )}
  </Box>
);

const StatistiquesPage = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_BASE}/api/recruteur-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setStats(data);
    };
    fetchStats();
  }, [API_BASE]);

  if (!stats)
    return (
      <LoadingPopup
        open={!stats}
        message="Chargement..."
      />
    );

  return (
    <Box>
      <HeaderRec />
      <Box className="container-vertical">
        {/* Titre principal */}
        <Typography variant="h3" align="center" gutterBottom className="main-title">
          Mes statistiques détaillées
        </Typography>
        <Divider className="section-divider" />

        {/* Niveau */}
        <Card className="card-section niveau-bg">
          <CardContent className="card-content-icon">
            <Box className="icon-box niveau-icon">
              <StarIcon fontSize="large" />
            </Box>
            <Box className="card-main-content">
              <Typography variant="subtitle1" className="section-title">
                Niveau actuel
              </Typography>
              <Typography variant="h6" color="primary" className="niveau-label">
                {stats.niveau?.label || "Débutant"}
              </Typography>
              <Typography variant="body2" color="textSecondary" className="niveau-description">
                {stats.niveau?.label === "Débutant"
                  ? "Aucun profil recruté pour le moment."
                  : stats.niveau?.label === "Intermédiaire"
                  ? "2 profils recrutés. Continuez pour monter de niveau !"
                  : stats.niveau?.label === "Confirmé"
                  ? "4 profils recrutés. Bravo !"
                  : stats.niveau?.label === "Expert"
                  ? "6 profils recrutés. Vous êtes expert reconnu."
                  : stats.niveau?.label === "Légende"
                  ? "10 profils recrutés ou plus. Vous faites partie des meilleurs recruteurs de la plateforme !"
                  : "Votre niveau évolue en fonction du nombre de profils recrutés sur la plateforme."}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Statistiques principales */}
        <Card className="card-section stats-bg">
          <CardContent className="card-content-icon">
            <Box className="icon-box stats-icon">
              <BarChartIcon fontSize="large" />
            </Box>
            <Box className="card-main-content wide">
              <Typography variant="subtitle1" className="section-title" gutterBottom>
                Statistiques principales
              </Typography>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    Missions prises : <b>{stats.missionsPrises}</b>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {stats.details?.missionsPrises?.length ? (
                    <PaginatedList
                      items={stats.details.missionsPrises}
                      renderItem={(m, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={m.titre} secondary={m.entreprise} />
                        </ListItem>
                      )}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucune mission.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    Candidatures présentées : <b>{stats.candidaturesPresentees}</b>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {stats.details?.candidaturesPresentees?.length ? (
                    <PaginatedList
                      items={stats.details.candidaturesPresentees}
                      renderItem={(c, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={c.nomProfil} secondary={c.missionTitre} />
                        </ListItem>
                      )}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucune candidature.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    En entretien : <b>{stats.enEntretien}</b>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {stats.details?.enEntretien?.length ? (
                    <PaginatedList
                      items={stats.details.enEntretien}
                      renderItem={(c, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={c.nomProfil} secondary={c.missionTitre} />
                        </ListItem>
                      )}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucun entretien.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    Recruté(e)s : <b>{stats.recrutes}</b>
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {stats.details?.recrutes?.length ? (
                    <PaginatedList
                      items={stats.details.recrutes}
                      renderItem={(c, i) => (
                        <ListItem key={i}>
                          <ListItemText primary={c.nomProfil} secondary={c.missionTitre} />
                        </ListItem>
                      )}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucun profil recruté.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Box>
          </CardContent>
        </Card>

        {/* Prime estimée */}
        <Card className="card-section prime-bg">
          <CardContent className="card-content-icon">
            <Box className="icon-box prime-icon">
              <MonetizationOnIcon fontSize="large" />
            </Box>
            <Box className="card-main-content">
              <Typography variant="subtitle1" className="section-title">
                Prime estimée
              </Typography>
              <MultiDeviseAmount montantObj={stats.primeTotale} />
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Détail des primes</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {stats.details?.primes?.length ? (
                    <PaginatedList
                      items={stats.details.primes}
                      renderItem={(p, i) => (
                        <ListItem key={i}>
                          <ListItemText
                            primary={`${p.montant} ${p.devise} — Mission : ${p.missionTitre}`}
                            secondary={`Profil proposé : ${p.nomProfil}`}
                          />
                        </ListItem>
                      )}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucune prime enregistrée.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
              <Typography variant="body2" color="textSecondary" className="section-note">
                Somme cumulée des primes sur toutes les missions pour lesquelles vous avez soumis des profils (toutes devises confondues). Une ligne par profil proposé.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Ma cagnotte */}
        <Card className="card-section cagnotte-bg">
          <CardContent className="card-content-icon">
            <Box className="icon-box cagnotte-icon">
              <AccountBalanceWalletIcon fontSize="large" />
            </Box>
            <Box className="card-main-content">
              <Typography variant="subtitle1" className="section-title">
                Ma cagnotte
              </Typography>
              <MultiDeviseAmount montantObj={stats.cagnotte} />
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Détail des gains</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {stats.details?.cagnotte?.length ? (
                    <PaginatedList
                      items={stats.details.cagnotte}
                      renderItem={(g, i) => (
                        <ListItem key={i}>
                          <ListItemText
                            primary={`${g.montant} ${g.devise} — Mission : ${g.missionTitre}`}
                            secondary={`Profil recruté : ${g.nomProfil}`}
                          />
                        </ListItem>
                      )}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Aucun gain enregistré.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
              <Typography variant="body2" color="textSecondary" className="section-note">
                Somme réellement obtenue pour les profils que vous avez proposés et qui ont été recrutés. Une ligne par profil recruté.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Footer />
    </Box>
  );
};

export default StatistiquesPage;
